import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

declare const _: any;

@Injectable({
  providedIn: 'root'
})
export class F4626Service {
  constructor(private http: HttpClient) { }

  fetchF4626Details(
    baseUrl: string,
    rowData: any,
    logged_in_user: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    // CRITICAL: Matching JsonObjectFactory.getJSONObj behavior exactly
    // JsonObjectFactory does: merge(filterParams, _params) then merge(globalParams, result)
    
    const jcd_key = rowData.JCD_KEY || 250;
    
    // Step 1: Define specific params for this API call
    const specificParams: any = {
      action_code: '77gopg',
      sso_id: logged_in_user,
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO,
      jcd_key: jcd_key,
      combination_key: rowData.COMBINATION_KEY,
      jcd_ta_key: null
    };

    // Step 2: Merge filterParams + specificParams (AngularJS: _.merge({}, filterParams, _params))
    const paramsMerge = _.merge({}, filterParams, specificParams);
    
    // Step 3: Merge globalParams + paramsMerge (AngularJS: _.merge({}, GlobalService.globalParams, paramsMerge))
    // globalParams now includes: screen_key, screen_type, activity_key, is_locked, issue_id, 
    // issue_id_for_tx_save, issue_short_desc (added in AngularJS controller)
    const finalParams = _.merge({}, globalService?.globalParams || {}, paramsMerge);
    
    // Fallback: Add missing parameters from rowData if they're not in globalParams
    // (for safety, though they should be in globalParams now)
    if (!finalParams.activity_key && rowData.ACTIVITY_KEY !== undefined) {
      finalParams.activity_key = rowData.ACTIVITY_KEY;
    }
    if (!finalParams.is_locked && rowData.IS_LOCKED !== undefined) {
      finalParams.is_locked = rowData.IS_LOCKED;
    }
    if (!finalParams.issue_id && rowData.ISSUE_ID !== undefined) {
      finalParams.issue_id = rowData.ISSUE_ID;
    }
    if (!finalParams.issue_id_for_tx_save && rowData.ISSUE_ID_FOR_TX_SAVE !== undefined) {
      finalParams.issue_id_for_tx_save = rowData.ISSUE_ID_FOR_TX_SAVE;
    }
    if (!finalParams.issue_short_desc && rowData.ISSUE_SHORT_DESC !== undefined) {
      finalParams.issue_short_desc = rowData.ISSUE_SHORT_DESC;
    }
    if (!finalParams.screen_key && rowData.SCREEN_KEY !== undefined) {
      finalParams.screen_key = rowData.SCREEN_KEY;
    }
    if (!finalParams.screen_type && rowData.SCREEN_TYPE !== undefined) {
      finalParams.screen_type = rowData.SCREEN_TYPE;
    }
    
    // Remove checkLockedbj if present (as AngularJS does)
    delete finalParams.checkLockedbj;

    // Convert params to URL query string for GET request (like AngularJS JsonObjectFactory)
    let queryParams = new URLSearchParams();
    Object.keys(finalParams).forEach(key => {
      if (finalParams[key] !== null && finalParams[key] !== undefined) {
        queryParams.set(key, finalParams[key].toString());
      }
    });

    const httpOptions = {
      withCredentials: true
    };

    // Use GET request like JsonObjectFactory.getJSONObj does
    return this.http.get<any>(`${baseUrl}/loadJsonObject?${queryParams.toString()}`, httpOptions);
  }

  saveForm4626(
    baseUrl: string,
    finalData: any[],
    clobSettings: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    // Build AngularJS-compatible payload structure
    // CRITICAL: Matching AngularJS exactly - it does:
    // var params = filterParams;
    // params = _.merge({}, GlobalService.globalParams, params);
    // params = _.extend({json_data:json_data}, params);
    // params = _.extend({json_settings:json_settings}, params);
    // params.process_name = "form-4626-save";
    
    const url = `${baseUrl}/saveJsonObject?action_code=sf2prg`;
    const json_settings = JSON.stringify(clobSettings);
    const json_data = JSON.stringify(finalData);

    // Step 1: Start with filterParams (which already contains parameters from AngularJS controller)
    let params = filterParams;
    
    // Step 2: Merge with globalParams (AngularJS: _.merge({}, GlobalService.globalParams, params))
    params = _.merge({}, globalService?.globalParams || {}, params);
    
    // Step 3: Extend with json_data
    params = _.extend({ json_data: json_data }, params);
    
    // Step 4: Extend with json_settings
    params = _.extend({ json_settings: json_settings }, params);
    
    // Step 5: Set process_name
    params.process_name = "form-4626-save";

    // CRITICAL: Add/override parameters that might not be in filterParams/globalParams
    // These are needed for the save call and must match AngularJS behavior
    
    // Ensure jcd_key is set
    if (!params.jcd_key) {
      params.jcd_key = rowData.JCD_KEY || filterParams.jcd_key || 250;
    }

    // Ensure tax_year and scenario are set
    if (!params.tax_year) {
      params.tax_year = rowData.TAX_YEAR || filterParams.tax_year;
    }
    if (!params.scenario) {
      params.scenario = rowData.SCENARIO || filterParams.scenario;
    }


    // CRITICAL: Set scenarioCode properly (using SCENARIO_TYPE_CODE or fallback to 'RAF')
    if (!params.scenarioCode) {
      params.scenarioCode = filterParams.scenarioCode || rowData.SCENARIO_TYPE_CODE || 'RAF';
    }

    // Ensure is_issue_key_enabled is set
    if (!params.is_issue_key_enabled) {
      params.is_issue_key_enabled = "N";
    }
    
    // Build checkLockedbj (always override to ensure correct structure including ME_CODE)
    const checkLockedbj = [{
      "leid": rowData.LEID || "",
      "me_code": rowData.ME_CODE || "",  // CRITICAL: Add ME_CODE
      "cdr_number": rowData.CDR_NO || rowData.MARS || "",
      "consol_group_key": rowData.CONSOL_GROUP_KEY || "",
      "reporting_period": rowData.REPORTING_PERIOD || "",
      "combination_key": rowData.COMBINATION_KEY || filterParams.combination_key || "",
      "scenario_key": parseInt(filterParams.scenario || params.scenario || '0')
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Convert params to URLSearchParams for form-urlencoded format (matching AngularJS $http POST)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}

